import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Section, Eyebrow } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Send, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Messages = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState({ messages: [], other: null });
  const [text, setText] = useState("");
  const endRef = useRef();

  useEffect(() => { if (!loading && !user) navigate("/"); }, [user, loading, navigate]);

  const loadConvos = useCallback(() => { api.get("/conversations").then((r) => setConvos(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user) loadConvos(); }, [user, loadConvos]);
  useEffect(() => { const to = params.get("to"); if (to) setActive(to); }, [params]);

  const loadThread = useCallback((other) => {
    if (!other) return;
    api.get(`/conversations/${other}/messages`).then((r) => setThread(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!active) return;
    loadThread(active);
    const t = setInterval(() => loadThread(active), 4000);
    return () => clearInterval(t);
  }, [active, loadThread]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const val = text; setText("");
    try { await api.post(`/conversations/${active}/messages`, { text: val }); loadThread(active); loadConvos(); } catch (e) {}
  };

  if (!user) return null;

  return (
    <Section className="py-10">
      <Eyebrow>Messages</Eyebrow>
      <div className="grid lg:grid-cols-3 gap-4 mt-4 h-[70vh]">
        <div className="eti-card rounded-lg overflow-y-auto eti-scroll" data-testid="conversation-list">
          {convos.length === 0 && <p className="p-5 text-sm text-slate-400">No conversations yet. Start one from any profile.</p>}
          {convos.map((c) => (
            <button key={c.user_id} onClick={() => setActive(c.user_id)} className={`w-full text-left p-4 border-b border-white/5 flex items-center gap-3 hover:bg-white/5 ${active === c.user_id ? "bg-white/5" : ""}`} data-testid={`conversation-${c.user_id}`}>
              <Avatar className="h-10 w-10"><AvatarImage src={c.avatar} /><AvatarFallback className="bg-primary/20 text-primary">{c.name?.[0]}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-white truncate">{c.name}</p><p className="text-xs text-slate-400 truncate">{c.last_text}</p></div>
              {c.unread > 0 && <span className="bg-primary text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center shrink-0">{c.unread}</span>}
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 eti-card rounded-lg flex flex-col" data-testid="message-thread">
          {active ? (
            <>
              <div className="p-4 border-b border-white/10 flex items-center gap-3">
                <Avatar className="h-9 w-9"><AvatarImage src={thread.other?.avatar} /><AvatarFallback className="bg-primary/20 text-primary">{thread.other?.name?.[0]}</AvatarFallback></Avatar>
                <div><p className="text-sm font-semibold text-white">{thread.other?.name}</p><p className="text-xs text-slate-400 capitalize">{thread.other?.role}</p></div>
              </div>
              <div className="flex-1 overflow-y-auto eti-scroll p-4 space-y-2">
                {thread.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_id === user.user_id ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.sender_id === user.user_id ? "bg-primary text-white" : "bg-secondary text-slate-200"}`}>{m.text}</div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-white/10 flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message..." className="bg-secondary border-white/10" data-testid="message-input" />
                <Button onClick={send} className="bg-primary hover:bg-primary/90 text-white" data-testid="message-send-button"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-500"><MessageSquare className="h-10 w-10 mb-2" /><p className="text-sm">Select a conversation</p></div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default Messages;
